import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  Button,
  IconButton,
  Divider,
  Typography,
  Stack,
  Paper,
  Chip,
  Box
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import LinkOffOutlinedIcon from "@mui/icons-material/LinkOffOutlined";
import { formatearNombreRol } from "../utils/formatters";

const UserFormDialog = ({ 
  open, 
  onClose, 
  onSave, 
  formData, 
  onChange, 
  isEdit = false,
  vinculos = [],
  onEditVinculo,
  onUnlinkVinculo,
}) => {
  
  // Colores dinámicos según la acción (Azul oscuro para crear, Azul medio para editar)
  const themeColor = isEdit ? "#3857a6" : "#031A7F";

  const handleSubmit = () => {
    onSave();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle
        sx={{
          backgroundColor: themeColor,
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontFamily: "Montserrat-Bold",
          fontSize: "1rem",
          px: 3,
          py: 2,
        }}
      >
        {isEdit 
          ? `Editar usuario: ${formData.nombre} ${formData.apellido}` 
          : "Crear nuevo usuario"}
        <IconButton onClick={onClose} sx={{ color: "#fff" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ px: 3, py: 3 }}>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {!isEdit && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="DNI"
                name="usuario"
                value={formData.usuario}
                onChange={onChange}
                placeholder="Inserta tu dni"
                inputProps={{
                  maxLength: 8,
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                }}
                helperText="El DNI debe tener 8 dígitos"
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <Typography
              sx={{
                fontFamily: "Montserrat-Bold",
                fontSize: "0.95rem",
                color: themeColor,
                mb: 1,
              }}
            >
              Datos personales
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Nombres"
              name="nombre"
              value={formData.nombre}
              onChange={onChange}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Apellidos"
              name="apellido"
              value={formData.apellido}
              onChange={onChange}
            />
          </Grid>

          {isEdit && (
            <Grid item xs={12}>
              <Box sx={{ mt: 1 }}>
                <Typography
                  sx={{
                    fontFamily: "Montserrat-Bold",
                    fontSize: "0.95rem",
                    color: themeColor,
                    mb: 1.5,
                  }}
                >
                  Instituciones y roles asociados
                </Typography>

                {vinculos.length > 0 ? (
                  <Stack spacing={1.25}>
                    {vinculos.map((vinculo) => (
                      <Paper
                        key={`${vinculo.usuario_id || formData.id}-${vinculo.institucion_id}`}
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          borderColor: "rgba(15,23,42,0.1)",
                        }}
                      >
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={1.5}
                          alignItems={{ xs: "flex-start", sm: "center" }}
                          justifyContent="space-between"
                        >
                          <Box>
                            <Typography sx={linkTitleStyles}>
                              {vinculo.institucion?.nombre || "Institución no disponible"}
                            </Typography>
                            <Typography sx={linkSubtitleStyles}>
                              {vinculo.institucion?.nivel_modalidad || "Nivel no disponible"}
                            </Typography>
                          </Box>

                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1}
                            alignItems={{ xs: "stretch", sm: "center" }}
                          >
                            <Chip
                              label={formatearNombreRol(vinculo.rol?.nombre)}
                              size="small"
                              sx={chipRoleStyles}
                            />
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<EditIcon />}
                              onClick={() => onEditVinculo?.(vinculo)}
                              sx={editLinkBtnStyles}
                            >
                              Editar vínculo
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<LinkOffOutlinedIcon />}
                              onClick={() => onUnlinkVinculo?.(vinculo)}
                              sx={unlinkLinkBtnStyles}
                            >
                              Desvincular
                            </Button>
                          </Stack>
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                ) : (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      borderColor: "rgba(15,23,42,0.1)",
                      backgroundColor: "#f8fafc",
                    }}
                  >
                    <Typography sx={emptyStateStyles}>
                      Este usuario aún no tiene instituciones asociadas.
                    </Typography>
                  </Paper>
                )}
              </Box>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={btnSecondaryStyles}
        >
          Cancelar
        </Button>

        <Button
          onClick={handleSubmit}
          variant="contained"
          sx={{
            ...btnPrimaryStyles,
            backgroundColor: themeColor,
            "&:hover": { backgroundColor: themeColor, filter: "brightness(0.8)" },
          }}
        >
          {isEdit ? "Guardar cambios" : "Registrar usuario"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** Estilos locales **/
const btnPrimaryStyles = {
  textTransform: "none",
  fontFamily: "Montserrat-Medium",
  borderRadius: "8px",
  px: 2.5,
  boxShadow: "none",
};

const btnSecondaryStyles = {
  textTransform: "none",
  fontFamily: "Montserrat-Medium",
  borderRadius: "8px",
  px: 2.5,
};

const linkTitleStyles = {
  fontFamily: "Montserrat-Bold",
  fontSize: "0.92rem",
  color: "#1e293b",
};

const linkSubtitleStyles = {
  fontFamily: "Montserrat-Regular",
  fontSize: "0.85rem",
  color: "#64748b",
};

const chipRoleStyles = {
  fontFamily: "Montserrat-Medium",
  backgroundColor: "rgba(56,87,166,0.10)",
  color: "#2f4c98",
};

const editLinkBtnStyles = {
  textTransform: "none",
  fontFamily: "Montserrat-Medium",
  borderRadius: "8px",
};

const unlinkLinkBtnStyles = {
  textTransform: "none",
  fontFamily: "Montserrat-Medium",
  borderRadius: "8px",
};

const emptyStateStyles = {
  fontFamily: "Montserrat-Regular",
  fontSize: "0.9rem",
  color: "#475569",
};

export default UserFormDialog;
